import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Copy, RefreshCw, RotateCcw, Pencil, Check, X, Settings2 } from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../../lib/api.js';
import { Card, LoadingPanel } from './_shared.jsx';

const SOURCE_LABELS = {
  meta:   'Meta',
  manual: 'Manual',
  google: 'Google',
  ringy:  'Ringy',
  csv:    'CSV',
};

function VendorWebhookRow({ vendor }) {
  const apiBase = (typeof window !== 'undefined' && window.location?.origin) || '';
  const url = vendor.webhookKey
    ? `${apiBase}/api/public-leads/vendor/${vendor._id}?key=${vendor.webhookKey}`
    : '';
  return (
    <div className="flex items-center gap-2 text-xs">
      <code className="text-white/60 font-mono truncate flex-1">{url || '(no webhook key)'}</code>
      {url && (
        <button
          className="px-2 py-1 rounded-sm border border-white/10 text-white/70 hover:bg-white/5"
          onClick={() => {
            navigator.clipboard.writeText(url);
            toast.success('Webhook URL copied');
          }}
        >
          <Copy className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function EditableName({ vendor, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(vendor.displayNameOverride || vendor.name || '');

  useEffect(() => {
    setDraft(vendor.displayNameOverride || vendor.name || '');
  }, [vendor._id, vendor.displayNameOverride, vendor.name]);

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-white">{vendor.displayNameOverride || vendor.name}</span>
        <button
          className="text-white/30 hover:text-white/70"
          onClick={() => setEditing(true)}
          title="Edit display name"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        className="input text-sm py-1 px-2"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
      />
      <button
        className="text-emerald-400 hover:text-emerald-300"
        onClick={async () => {
          await onSave({ displayNameOverride: draft });
          setEditing(false);
        }}
      >
        <Check className="w-4 h-4" />
      </button>
      <button
        className="text-white/40 hover:text-white/70"
        onClick={() => setEditing(false)}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function LeadVendorsTab() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState(null);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await apiGet('/lead-vendors');
      if (r?.ok) setVendors(r.vendors || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load vendors');
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!vendors) return null;
    if (filter === 'all') return vendors;
    return vendors.filter((v) => (v.source || 'manual') === filter);
  }, [vendors, filter]);

  async function syncMetaVendors() {
    setBusy(true);
    try {
      const r = await apiPost('/meta-ads/sync-vendors');
      if (r?.ok) {
        toast.success(`Sync done — ${r.summary.created} new, ${r.summary.updated} updated, ${r.summary.archivedStale} archived`);
        await load();
      }
    } catch (e) {
      toast.error(e.message || 'Sync failed');
    } finally {
      setBusy(false);
    }
  }

  async function updateVendor(id, patch) {
    try {
      const r = await apiPut(`/lead-vendors/${id}`, patch);
      if (r?.ok) {
        toast.success('Vendor updated');
        await load();
      }
    } catch (e) {
      toast.error(e.message || 'Update failed');
    }
  }

  async function resetDisplayName(id) {
    try {
      const r = await apiPost(`/lead-vendors/${id}/reset-display-name`);
      if (r?.ok) {
        toast.success('Reset to Meta name');
        await load();
      }
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    }
  }

  if (!vendors) return <LoadingPanel label="Loading lead vendors…" />;

  const counts = {
    all: vendors.length,
    meta: vendors.filter((v) => v.source === 'meta').length,
    manual: vendors.filter((v) => !v.source || v.source === 'manual').length,
  };

  return (
    <div className="space-y-4">
      <Card
        title="Lead Vendors"
        description="One vendor per Meta ad set is auto-created when you connect Meta. Manual vendors created from the Vendors page also appear here."
        actions={
          <button
            className="btn-secondary text-sm flex items-center gap-2"
            onClick={syncMetaVendors}
            disabled={busy}
          >
            <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
            Sync Meta Ad Sets
          </button>
        }
      >
        <div className="flex gap-2 mb-4">
          {['all', 'meta', 'manual'].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-md text-sm border ${
                filter === k
                  ? 'bg-civix-purple/20 text-white border-civix-purple/40'
                  : 'border-white/10 text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              {k.charAt(0).toUpperCase() + k.slice(1)} ({counts[k] ?? 0})
            </button>
          ))}
        </div>

        {(!filtered || filtered.length === 0) ? (
          <div className="text-center py-8 text-white/40 text-sm">
            No vendors yet. Connect Meta on the Meta Ads page to auto-create one per ad set.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/50 border-b border-white/10">
                  <th className="py-2 px-2">Name</th>
                  <th className="py-2 px-2">Source</th>
                  <th className="py-2 px-2">Ad Set</th>
                  <th className="py-2 px-2">Page</th>
                  <th className="py-2 px-2 text-right">30d</th>
                  <th className="py-2 px-2 text-right">All-time</th>
                  <th className="py-2 px-2">Active</th>
                  <th className="py-2 px-2">Webhook</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => (
                  <tr key={v._id} className="border-b border-white/5 align-middle">
                    <td className="py-2 px-2">
                      <EditableName
                        vendor={v}
                        onSave={(patch) => updateVendor(v._id, patch)}
                      />
                    </td>
                    <td className="py-2 px-2">
                      <span className="pill bg-white/5 text-white/70 border-white/10">
                        {SOURCE_LABELS[v.source || 'manual'] || v.source}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-white/70">
                      {v.metaAdsetName ? (
                        <div>
                          <div>{v.metaAdsetName}</div>
                          <div className="text-xs text-white/30 font-mono">{v.metaAdsetId}</div>
                        </div>
                      ) : <span className="text-white/30">—</span>}
                    </td>
                    <td className="py-2 px-2 text-white/70">{v.metaPageName || '—'}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{v.leadCount30d || 0}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{v.leadCount || 0}</td>
                    <td className="py-2 px-2">
                      <input
                        type="checkbox"
                        checked={!!v.active}
                        onChange={(e) => updateVendor(v._id, { active: e.target.checked })}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="py-2 px-2 max-w-xs">
                      <VendorWebhookRow vendor={v} />
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          className="btn-ghost text-xs flex items-center gap-1"
                          onClick={() => navigate(`/vendors/edit/${v._id}`)}
                          title="Edit vendor & field mapping"
                        >
                          <Settings2 className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        {v.source === 'meta' && v.displayNameOverride && (
                          <button
                            className="text-xs text-white/50 hover:text-white flex items-center gap-1"
                            onClick={() => resetDisplayName(v._id)}
                            title="Reset to Meta source name"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Reset
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
